import { fileUrl, ratingMeta, SEBA_IMG } from "@/lib/api";
import { Wind, Clock, MapPin, Waves } from "lucide-react";

const colorMap = {
  sky: "bg-sky-100 text-sky-700",
  cyan: "bg-cyan-100 text-cyan-700",
  emerald: "bg-emerald-100 text-emerald-700",
  orange: "bg-orange-100 text-orange-700",
  red: "bg-red-100 text-red-700",
  slate: "bg-slate-100 text-slate-600",
};

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "adesso";
    if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
    return `${Math.floor(diff / 86400)} g fa`;
  } catch {
    return "";
  }
}

export function UpdatesFeed({ updates = [] }) {
  if (!updates.length) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20" data-testid="updates-empty">
        <Waves className="h-14 w-14 text-sky-200 mx-auto mb-4 animate-floaty" />
        <p className="font-head text-xl font-semibold text-slate-700">Acque calme.</p>
        <p className="text-slate-400">In attesa dell'aggiornamento di Seba...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="updates-feed">
      {updates.map((u) => {
        const meta = ratingMeta(u.wind_rating);
        return (
          <article
            key={u.id}
            className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200"
            data-testid={`update-card-${u.id}`}
          >
            <div className="flex items-center gap-3 p-4">
              <img src={SEBA_IMG} alt="Seba" className="h-11 w-11 rounded-full object-cover ring-2 ring-sky-100" />
              <div className="flex-1">
                <div className="font-head font-bold text-slate-900 flex items-center gap-2">
                  {u.author || "Seba"}
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-sky-600 text-white px-2 py-0.5 rounded-full">Live</span>
                </div>
                <div className="text-xs text-slate-400 flex items-center gap-2">
                  <Clock className="h-3 w-3" /> {timeAgo(u.created_at)}
                  {u.spot_name && (
                    <>
                      <span>·</span>
                      <MapPin className="h-3 w-3" /> {u.spot_name}
                    </>
                  )}
                </div>
              </div>
              <span className={`text-xs font-bold px-3 py-1 rounded-full ${colorMap[meta.color]}`}>
                {meta.label}
              </span>
            </div>

            {u.image_path && (
              <img
                src={fileUrl(u.image_path)}
                alt={u.title}
                className="w-full max-h-[60vh] object-cover"
              />
            )}

            <div className="p-5">
              <h3 className="font-head text-xl font-semibold text-slate-900">{u.title}</h3>
              {(u.wind_knots != null || u.wind_direction) && (
                <div className="flex items-center gap-2 mt-1 text-sky-700 font-bold">
                  <Wind className="h-4 w-4" />
                  {u.wind_knots != null && <span>{u.wind_knots} nodi</span>}
                  {u.wind_direction && <span>· {u.wind_direction}</span>}
                </div>
              )}
              <p className="text-slate-600 mt-2 leading-relaxed whitespace-pre-wrap">{u.note}</p>
            </div>
          </article>
        );
      })}
    </div>
  );
}
